import { useEffect, useState } from 'react';

import i18n from 'i18n-react';

import APIHandler from 'config/APIHandler';

const typePriority = {
  alert: 0,
  message: 1,
  contest: 2,
  badge: 3,
  'content-update': 4,
  events: 5,
};

/**
 * @param a
 * @param b
 */
const compareTypePriority = (a, b) => {
  const aPriority = typePriority[a.type] ?? 99;
  const bPriority = typePriority[b.type] ?? 99;
  return aPriority - bPriority;
};

/** @param value */
const isRecord = (value) => typeof value === 'object' && value !== null;

/** @param raw */
const normalizeAnnouncements = (raw) => {
  if (!Array.isArray(raw)) {
    return [];
  }
  const out = [];
  for (const item of raw) {
    if (!isRecord(item)) {
      continue;
    }
    const { id } = item;
    const { message } = item;
    if ((typeof id !== 'number' && typeof id !== 'string') || typeof message !== 'string') {
      continue;
    }
    const type = typeof item.type === 'string' ? item.type : 'message';
    const weblink = typeof item.weblink === 'string' ? item.weblink : '';
    out.push({ id, type, message, weblink });
  }
  return out;
};

/**
 * Loads CMS New & Now notifications (APIHandler.getAnnouncement) for the glass panel.
 *
 * @param appId
 */
const useNewAndNowNotifications = (appId) => {
  const [notifications, setNotifications] = useState([]);
  const [isLoading, setIsLoading] = useState(() => appId > 0);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    if (appId <= 0) {
      setNotifications([]);
      setIsLoading(false);
      setError(null);
      return undefined;
    }

    setIsLoading(true);
    setError(null);

    void (async () => {
      try {
        const res = await APIHandler.getAnnouncement(appId);
        if (cancelled) {
          return;
        }
        const list = normalizeAnnouncements(res);
        const sorted = list.length > 1 ? [...list].sort(compareTypePriority) : list;
        setNotifications(sorted);
      } catch {
        if (!cancelled) {
          setError(i18n.translate('glassWidgets.errors.requestFailed') ?? 'Something went wrong.');
          setNotifications([]);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [appId]);

  return { notifications, isLoading, error };
};

export default useNewAndNowNotifications;
